import React, { useCallback, useEffect, useState } from 'react';
import { Form, Select, Input, Button, message, Alert } from 'antd';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import apiCalls from 'src/api/apiCalls';
import Loader from '../loader.component';
import { useParams } from 'react-router-dom';
import { NumericFormat } from 'react-number-format';

const { Option } = Select;
  
const EnergyRechargeForm = (props) => {
  const [form] = Form.useForm();
  const [transactionQuantity, setTransactionQuantity] = useState(0);
  const [addressTypes, setAddressTypes] = useState([]);
  const [loader, setLoader] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [btnLoader, setBtnLoader] = useState(false);
  const params = useParams();
  const tenantId = params?.tenantId;
  const { availableCredit, setIsEnergyRechargeGridRefresh } = props?.creditData;
  

  const onFinish = useCallback(async (values) => {
    if (values?.transaction <= 0) {
      setErrorMessage('Transaction quantity must be greater than 0.');
      return;
    }
    if (values?.transaction > 5) {
      setErrorMessage('Transaction quantity must be below 5 or equal.');
      return;
    }
    try {
      setErrorMessage(null);
      setBtnLoader(true);
      const obj = {
        address: values?.address,
        count: values?.transaction ? parseInt(values.transaction, 10) : 0,
      };
      const response = await apiCalls.saveRechargeEnergy(tenantId, obj);
      if (response.ok) {
        message.success('Recharge successfully!');
        form.resetFields();
        setTransactionQuantity('');
        setErrorMessage(null);
        setBtnLoader(false);
        setIsEnergyRechargeGridRefresh(true);
      } else {
        setBtnLoader(false);
        setErrorMessage(response.error);
      }
    } catch (error) {
      setErrorMessage(error.message || 'An unexpected error occurred.');
      setBtnLoader(false);
    }
  }, [form, tenantId, setIsEnergyRechargeGridRefresh]);

  const updateTransactionAndValidate = useCallback((newValue) => {
    const newValueStr = String(newValue);
    setTransactionQuantity(newValueStr);
    form.setFieldsValue({ transaction: newValueStr });
    form.validateFields(['transaction']);
  }, [form]);

  const handleDecrement = useCallback(() => {
    const currentValueStr = form.getFieldValue('transaction') || '0';
    const currentNum = /^\d+$/.test(currentValueStr) ? parseInt(currentValueStr, 10) : 0;
    const newValue = Math.max(0, currentNum - 1); // Prevent negative values
    updateTransactionAndValidate(newValue);
  }, [form, updateTransactionAndValidate]);

  const handleIncrement = useCallback(() => {
    const currentValueStr = form.getFieldValue('transaction') || '0';
    const currentNum = /^\d+$/.test(currentValueStr) ? parseInt(currentValueStr, 10) : 0;
    const newValue = currentNum + 1; // Allow unrestricted increment
    updateTransactionAndValidate(newValue);
  }, [form, updateTransactionAndValidate]);

  const handleInputChange = useCallback((e) => {
    const inputValue = e.target.value;

    if (inputValue === '') {
      setTransactionQuantity('');
      form.setFieldsValue({ transaction: '' });
      form.validateFields(['transaction']);
      return;
    }
    if (/^\d*$/.test(inputValue)) {
      setTransactionQuantity(inputValue);
      form.setFieldsValue({ transaction: inputValue });
      form.validateFields(['transaction']);
    } else {
      form.setFieldsValue({ transaction: transactionQuantity });
      form.validateFields(['transaction']);
    }
  }, [form, transactionQuantity]);

  const getAddressTypes = useCallback(async () => {
    setErrorMessage(null);
    setLoader(true);
    try {
      const response = await apiCalls.getAddressTypes(tenantId);
      if (response.ok) {
        setAddressTypes(response?.data || []);
        setErrorMessage(null);
        setLoader(false);
      } else {
        setLoader(false);
        setErrorMessage(response?.error);
      }
    } catch (error) {
      setErrorMessage(error);
      setLoader(false);
    }
  }, [tenantId]);

  useEffect(() => {
    getAddressTypes();
  }, [getAddressTypes]);

  const handleChange = useCallback((value) => {
    form.setFieldsValue({ address: value });
    form.validateFields(['address']);
  }, [form]);

  const closeErrorMessage = useCallback(() => {
    setErrorMessage(null);
  }, []);

  return (
    <div className="energy-recharge-container summary-container p-3 mt-4">
      <h2 level={4} className="sub-titles mb-4">Energy Recharge</h2>
      {errorMessage && (
        <Alert
          type="error"
          description={errorMessage}
          onClose={closeErrorMessage}
          showIcon
          closable
        />
      )}
      {loader ? (
        <Loader />
      ) : (
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <div className="cust-input mt-4 custom-input-field">
            <Form.Item
              className="input-label floatinput-label mb-3"
              label="Address"
              name="address"
              rules={[{ required: true, message: 'Is required!' }]}
            >
              <Select
                className=""
                placeholder={`Select Address`}
                onChange={handleChange}
              >
                {addressTypes?.map((item) => (
                  <Option value={item?.address} key={item?.id}>
                    {item?.address}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>
          <div className="cust-input cust-textArea custom-input-field mt-4">
            <Form.Item
              className="input-label floatinput-label mb-1"
              label="Transaction"
              name="transaction"
              rules={[
                { required: true, message: 'Is required!' },
                {
                  validator: (_, value) => {
                    const stringValue = String(value ?? '');

                    if (stringValue.trim() === '') {
                      return Promise.resolve();
                    }
                    if (!/^\d+$/.test(stringValue)) {
                      return Promise.reject('Please enter numbers only.');
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                placeholder="Please enter"
                className="custom-energy-input"
                prefix={
                  <Button
                    icon={<MinusOutlined className="text-info fw-medium" />}
                    onClick={handleDecrement}
                    type="button"
                  />
                }
                suffix={
                  <Button
                    icon={<PlusOutlined className="text-info fw-medium" />}
                    onClick={handleIncrement}
                    type="button"
                  />
                }
                onChange={handleInputChange}
                autoComplete="off"
              />
            </Form.Item>
          </div>
          <div className="d-flex justify-content-between align-items-center mb-6">
          <p className="text-secondary fw-medium">Max Recharge Quantity:{" "}
                <NumericFormat
                  thousandSeparator
                  className=""
                  value={availableCredit?.data?.creditLimit || 0}
                  decimalScale={4}
                  displayType="text"
                />
             </p>
            <p className="text-secondary fw-medium">
              Available Credit:{" "}
              <NumericFormat
                  thousandSeparator
                  className=""
                  value={availableCredit?.data?.availableCredit || 0}
                  decimalScale={4}
                  displayType="text"
                />
            </p>
          </div>

          <div className="mt-2">
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                block
                className="primary-btn w-full"
                loading={btnLoader}
              >
                Recharge
              </Button>
            </Form.Item>
          </div>

          <div className="disclaimer-container">
            <p className="text-secondary font-normal">
              Energy must be used within{' '}
              <span className="text-info fw-medium">10 minutes</span>. otherwise,
              it will be reclaimed.
            </p>
          </div>
        </Form>
      )}
    </div>
  );
};

export default EnergyRechargeForm;